import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  cn,
  Link,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@beweegbaas/ui";
import { createFileRoute, getRouteApi, redirect } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { motion } from "motion/react";
import { useEffect, useState } from "react";
import { Page } from "#/components/page";
import { GoalsCheckboxGroup } from "#/modules/les.start-flow/components/goals-checkbox-group";
import { StartLessonSummary } from "#/modules/les.start-flow/components/summary.block";
import { SubmitButton } from "#/components/submit-button";
import type { StartLessonFilters } from "#/modules/les.start-flow/filters";
import { precheckLesFlowGoals } from "#/modules/les.start-flow/les-flow.functions";
import { isApiFailure } from "#/modules/util.errors/translate";
import { useDebouncedSearchCommit } from "#/modules/util.helpers/use-debounced-search-commit";
import type { components } from "#/schema";

const lesFlowRouteApi = getRouteApi(
  "/$locale/{-$schoolyear}/_authenticated/$school/start/les-flow",
);

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/start/les-flow/2",
)({
  // Step 2 needs at least one chosen class; without one, back to step 1.
  beforeLoad: ({ search, params }) => {
    if (search.klasIds.length === 0) {
      throw redirect({
        to: "/$locale/{-$schoolyear}/$school/start/les-flow/1",
        params,
        search,
      });
    }
  },
  component: LesFlowStep2Page,
});

function LesFlowStep2Page() {
  const search = Route.useSearch();
  const params = Route.useParams();
  const navigate = Route.useNavigate();
  const { klassen, beweegactiviteitenreeksen, socialeThemas } = lesFlowRouteApi.useLoaderData();
  const precheckGoals = useServerFn(precheckLesFlowGoals);

  const setFilters = (patch: Partial<StartLessonFilters>) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true, resetScroll: false });

  // Multiple selected klassen can span multiple leerjaarcombos; highlight them all.
  const selectedKlasLeerjaarCombos = klassen
    .filter((klas) => search.klasIds.includes(klas.id))
    .flatMap((klas) => klas.leerjaarCombos);

  const [deletions, setDeletions] = useState<
    components["schemas"]["UpdateLesPrecheckResponse"] | null
  >(null);

  // Local state + debounced commit for the two goal-search text inputs: keeps keystrokes off the
  // URL/loader so typing doesn't refire setFilters (and the les-flow loader) on every character.
  const [beweegSearchValue, setBeweegSearchValue] = useDebouncedSearchCommit(
    search.beweegactiviteitenreeksSearchTerm ?? "",
    (value) => setFilters({ beweegactiviteitenreeksSearchTerm: value }),
    300,
  );
  const [sociaalSearchValue, setSociaalSearchValue] = useDebouncedSearchCommit(
    search.sociaalEmotioneelThemasReeksSearchTerm ?? "",
    (value) => setFilters({ sociaalEmotioneelThemasReeksSearchTerm: value }),
    300,
  );

  const submitGoals = async (confirmDeletions = false) => {
    // In edit mode, goals removed from a les that already has results need explicit
    // confirmation before continuing to step 3.
    if (search.editLesId && !confirmDeletions) {
      const precheck = await precheckGoals({
        data: { lesId: search.editLesId, filters: search },
      });
      if (!precheck || isApiFailure(precheck)) return;
      if (
        precheck.beweegactiviteitenToRemove.length > 0 ||
        precheck.sociaalEmotioneelThemasToRemove.length > 0
      ) {
        setDeletions(precheck);
        return;
      }
    }
    void navigate({
      to: "/$locale/{-$schoolyear}/$school/start/les-flow/3",
      search: (prev) => prev,
    });
  };

  const [showStickyNext, setShowStickyNext] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowStickyNext(window.scrollY > 500);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      {/* Kept mounted so the close transition can play; content null-guards `deletions`. */}
      <AlertDialog
        open={!!deletions}
        onOpenChange={(open) => {
          if (!open) setDeletions(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{m["common.attention"]()}</AlertDialogTitle>
            <AlertDialogDescription>
              {m["les.start.deletion_warning.confirm_message"]()}
            </AlertDialogDescription>
          </AlertDialogHeader>

          <ul
            className="bg-muted text-muted-foreground mt-3 flex flex-col gap-1
                rounded-md p-2"
          >
            {deletions?.beweegactiviteitenToRemove.map((activity) => (
              <li key={activity.beweegactiviteitId} className="flex items-center">
                {activity.name} (
                {m["les.start.deletion_warning.results_count"]({
                  count: activity.leerlingen.length,
                })}
                )
              </li>
            ))}
            {deletions?.sociaalEmotioneelThemasToRemove.map((theme) => (
              <li key={theme.sociaalEmotioneelThemaId} className="flex items-center">
                {theme.name} (
                {m["les.start.deletion_warning.results_count"]({
                  count: theme.leerlingen.length,
                })}
                )
              </li>
            ))}
          </ul>

          <AlertDialogFooter>
            <AlertDialogCancel variant="outline" size="default">
              {m["common.actions.cancel"]()}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setDeletions(null);
                void submitGoals(true);
              }}
            >
              {m["common.actions.next"]()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <form
        id="goals-form"
        onSubmit={(event) => {
          event.preventDefault();
          void submitGoals();
        }}
        className="flex flex-col gap-0"
      >
        <Tabs
          onValueChange={(value) => setFilters({ doelenTab: String(value) })}
          value={search.doelenTab}
          className={"gap-0"}
          variant="line"
        >
          <Page>
            <Page.Header>
              <Page.Title>{m["les.start.choose_your_goals"]()}</Page.Title>

              <Page.Header.Actions>
                {search.editLesId == null && (
                  <Page.Header.BackAction>
                    <Link
                      variant="muted"
                      size="sm"
                      underline={true}
                      className={"mr-2"}
                      to="/$locale/{-$schoolyear}/$school/start/les-flow/1"
                      params={params}
                      search={search}
                    >
                      {m["common.actions.back"]()}
                    </Link>
                  </Page.Header.BackAction>
                )}

                <Page.Header.MainAction>
                  <SubmitButton size={"xl"} type="submit" className={"max-sm:w-full"}>
                    {m["common.actions.next"]()}
                    <IconMaterialSymbolsArrowRightAltRounded />
                  </SubmitButton>
                </Page.Header.MainAction>
              </Page.Header.Actions>
            </Page.Header>
            <Page.Subnavigation>
              <TabsList aria-label={m["les.start.beweegactiviteitenreeksen"]()}>
                <TabsTrigger value="beweegactiviteitenreeksen">
                  {m["les.start.beweegactiviteitenreeksen"]()}
                  {search.beweegactiviteiten.length > 0 && (
                    <span
                      className={`bg-muted ml-2 inline-flex aspect-square
                      size-4 items-center justify-center rounded-full
                      text-xs`}
                    >
                      {search.beweegactiviteiten.length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="sociaal-emotionele-themas">
                  {m["common.labels.sociaal_emotionele_themas"]()}
                  {search.sociaalEmotioneelThemas.length > 0 && (
                    <span
                      className={`bg-muted ml-2 inline-flex aspect-square
                      size-4 items-center justify-center rounded-full
                      text-xs`}
                    >
                      {search.sociaalEmotioneelThemas.length}
                    </span>
                  )}
                </TabsTrigger>
              </TabsList>
            </Page.Subnavigation>

            <Page.Container>
              <Page.Body>
                <Page.SidebarLayout>
                  <div>
                    <TabsContent value="beweegactiviteitenreeksen">
                      <GoalsCheckboxGroup
                        type="beweegactiviteitenreeksen"
                        data={beweegactiviteitenreeksen}
                        selectedValues={search.beweegactiviteiten}
                        onSelectionChange={(value) => setFilters({ beweegactiviteiten: value })}
                        selectedKlasLeerjaarCombos={selectedKlasLeerjaarCombos}
                        name="selectedBeweegactiviteiten"
                        ariaLabel={`Selecteer ${m["les.start.beweegactiviteitenreeksen"]().toLowerCase()}`}
                        searchValue={beweegSearchValue}
                        onSearchChange={setBeweegSearchValue}
                        includeDefaults={search.includeDefaultsBeweeg ?? true}
                        onIncludeDefaultsChange={(value: boolean) =>
                          setFilters({ includeDefaultsBeweeg: value })
                        }
                      />
                    </TabsContent>
                    <TabsContent value="sociaal-emotionele-themas" className={"pb-10"}>
                      <GoalsCheckboxGroup
                        type="sociaal-emotionele-themas"
                        data={socialeThemas}
                        selectedValues={search.sociaalEmotioneelThemas}
                        onSelectionChange={(value) =>
                          setFilters({ sociaalEmotioneelThemas: value })
                        }
                        selectedKlasLeerjaarCombos={selectedKlasLeerjaarCombos}
                        name="selectedSocialGoals"
                        ariaLabel={`Selecteer ${m["common.labels.sociaal_emotionele_themas"]().toLowerCase()}`}
                        searchValue={sociaalSearchValue}
                        onSearchChange={setSociaalSearchValue}
                        includeDefaults={search.includeDefaultsSociaal ?? true}
                        onIncludeDefaultsChange={(value: boolean) =>
                          setFilters({ includeDefaultsSociaal: value })
                        }
                      />
                    </TabsContent>

                    <div
                      className={cn(
                        "sticky bottom-0 z-10 grid grid-cols-12 gap-y-4 md:gap-x-10 md:gap-y-10",
                        "[container-type:scroll-state] [container-name:sticky-next-bar]",
                      )}
                    >
                      <style>{`
                    @supports (container-type: scroll-state) {
                      .sticky-next-bar-inner {
                        padding-inline: 0;
                      }
                      @container sticky-next-bar scroll-state(stuck: bottom) {
                        .sticky-next-bar-inner {
                          padding-inline: 1rem;
                        }
                      }
                    }

                    @not (supports (container-type: scroll-state)) {
                      .sticky-next-bar-inner {
                        padding-inline: 1rem;
                      }
                    }
                  `}</style>
                      <div className="col-span-2 hidden @6xl/sidebar-layout:block"></div>
                      <div
                        className={cn(
                          "sticky-next-bar-inner col-span-12 transition-all @6xl/sidebar-layout:col-span-10",
                          "mt-4 flex items-center px-4 py-4 md:justify-end",
                        )}
                      >
                        <motion.div
                          className="pointer-events-none absolute inset-0 mask-t-from-50%
                        mask-l-from-50% mask-l-to-90% backdrop-blur-md"
                          aria-hidden
                          initial={{ opacity: 0 }}
                          transition={{
                            delay: showStickyNext ? 0 : 0.4,
                            duration: 1,
                          }}
                          animate={{ opacity: showStickyNext ? 1 : 0 }}
                        />

                        <motion.div
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{
                            opacity: showStickyNext ? 1 : 0,
                            scale: showStickyNext ? 1 : 0.9,
                            transition: { delay: showStickyNext ? 0.5 : 0 },
                          }}
                        >
                          <SubmitButton
                            form="goals-form"
                            type="submit"
                            size="xl"
                            className="relative z-10 min-w-[200px]"
                          >
                            {m["common.actions.next"]()}
                            <IconMaterialSymbolsArrowRightAltRounded />
                          </SubmitButton>
                        </motion.div>
                      </div>
                    </div>
                  </div>
                  <Page.Sidebar className="@max-4xl/sidebar-layout:hidden @6xl/sidebar-layout:max-w-[320px]">
                    <StartLessonSummary className={"sticky top-4"} />
                  </Page.Sidebar>
                </Page.SidebarLayout>
              </Page.Body>
            </Page.Container>
          </Page>
        </Tabs>
      </form>
    </>
  );
}
